import React, { createContext, useContext, useMemo, ReactNode } from 'react'

import useLeadFormConfigQuery from 'queries/lead-form-config'
import { LeadFormConfig, LeadFormConfigCondition } from 'services/types/lead'

export type RequiredField = {
  field: string
  required: boolean
}

export interface LeadFormConfigContextValue {
  requiredFields: RequiredField[]
  requiredFieldNames: string[]
  isLoading: boolean
  error?: Error | null
  data?: LeadFormConfig[]
  setConditionData?: React.Dispatch<React.SetStateAction<LeadFormConfigCondition | null>>
}

const LeadFormConfigContext = createContext<LeadFormConfigContextValue | undefined>(undefined)

export interface LeadFormConfigProviderProps {
  initialConditionData?: LeadFormConfigCondition
  children: ReactNode
}

export const LeadFormConfigProvider: React.FC<LeadFormConfigProviderProps> = ({
  children,
  initialConditionData = null,
}) => {
  const { data, isLoading, error } = useLeadFormConfigQuery()
  const [conditionData, setConditionData] = React.useState<LeadFormConfigCondition | null>(initialConditionData)

  const requiredFields = useMemo(() => {
    if (!data) return []

    const matchedRules = data.filter((rule: LeadFormConfig) => {
      if (!rule.condition) return true

      if (!rule.condition.lead_type_id) return true

      return rule.condition.lead_type_id === conditionData?.lead_type_id
    })

    // Type-specific rules override global (condition: null). Empty configs => no required fields.
    const [specificRules, globalRules] = matchedRules.reduce<[LeadFormConfig[], LeadFormConfig[]]>(
      ([specific, global], rule) => {
        if (rule.condition?.lead_type_id) specific.push(rule)
        else global.push(rule)
        return [specific, global]
      },
      [[], []],
    )

    const configs = [...specificRules, ...globalRules].flatMap((rule) => rule.configs || [])

    const uniqueConfigsMap = new Map<string, RequiredField>()

    return configs.reduce<RequiredField[]>((acc, config) => {
      if (!uniqueConfigsMap.has(config.field)) {
        uniqueConfigsMap.set(config.field, config)
        acc.push(config)
      }
      return acc
    }, [])
  }, [conditionData?.lead_type_id, data])

  // Only explicit required: true entries — empty LEAD_FORM_CONFIG does not require campaign.
  const requiredFieldNames: string[] = useMemo(
    () => requiredFields.filter((field) => field.required).map((field) => field.field),
    [requiredFields],
  )

  const contextValue = useMemo(
    () => ({
      requiredFields,
      requiredFieldNames,
      isLoading,
      error,
      data,
      setConditionData,
    }),
    [requiredFields, requiredFieldNames, isLoading, error, data],
  )

  return <LeadFormConfigContext.Provider value={contextValue}>{children}</LeadFormConfigContext.Provider>
}

export const useLeadFormConfigContext = (): LeadFormConfigContextValue => {
  const context = useContext(LeadFormConfigContext)

  if (context === undefined) {
    throw new Error('useLeadFormConfigContext must be used within a LeadFormConfigProvider')
  }

  return context
}

export default LeadFormConfigContext
